/* ─────────────────────────────────────────────────────────────────────────
   Customer field validation — phone & email.
   Mirrors the backend so client-side checks stay consistent with the server
   (PhoneNumber::normalize() + email lowercase/trim).
   ───────────────────────────────────────────────────────────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Normalize a phone number to canonical digits — mirrors the backend's
 * PhoneNumber::normalize(): strips spaces, dashes, brackets and "+", and
 * drops a leading "1" country code from 11-digit North-American numbers.
 *   "+1 (647) 555-1234" → "6475551234"
 */
export function normalizePhone(raw: string): string {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits;
}

/**
 * Validate a customer phone number. Accepts "+1", spaces, brackets and dashes
 * — validation runs against the NORMALIZED digits.
 *   • exactly 10 digits → valid
 *   • fewer than 10     → still incomplete, no error shown while typing
 *   • more than 10      → "Enter a valid phone number"
 * Empty returns no inline error (required-ness is enforced separately by the
 * form's Save guard).
 */
export function validateCustomerPhone(raw: string): string | undefined {
  if (!raw || !raw.trim()) return undefined;
  return normalizePhone(raw).length > 10 ? "Enter a valid phone number" : undefined;
}

/**
 * Validate a customer email. Trims surrounding whitespace and lowercases
 * before checking the format. Empty is allowed (email is optional).
 */
export function validateCustomerEmail(raw: string): string | undefined {
  const email = (raw || "").trim().toLowerCase();
  if (!email) return undefined;
  return EMAIL_RE.test(email) ? undefined : "Enter a valid email address";
}
